import { Link, VStack } from '@chakra-ui/react';

import {
  CapitalDeposit,
  CompanyCreationSasu,
  CompanyCreationStatus,
  CompanyLegalBeneficiary,
  CompanyProfile,
  KybDocument,
  UserProfile,
} from '__generated__/GQL';
import asBentoPage from 'common/bento/hoc/asBentoPage';
import webView from 'common/webView';
import Text from 'components/_core/Text';
import { INTERCOM_LAUNCHER_PROPS } from 'features/Intercom';
import OBNavigation from 'features/Onboarding/components/OBNavigation/OBNavigation';

import Header from '../../../../components/Header';
import { applicationAccountOpeningStep } from './applicationSteps/applicationAccountOpening';
import { applicationFundsStep } from './applicationSteps/applicationFunds';
import { applicationSentToStateStep } from './applicationSteps/applicationSentToState';
import { applicationSignatureStep } from './applicationSteps/applicationSignature';
import { applicationVerificationStep } from './applicationSteps/applicationVerification';
import { ApplicationStepCard } from './components/ApplicationStepCard';
import locales from './locales';

export type ApplicationFollowUpData = {
  userProfile: Pick<UserProfile, 'email' | 'uid' | 'firstName' | 'lastName'>;
  companyProfile: Pick<CompanyProfile, 'companyProfileId' | 'legalName'> & {
    legalNumberOfShares: number;
    legalShareCapital: number;
  };
  creationSASU: Pick<CompanyCreationSasu, 'status'> | null;
  kybDocuments: Array<
    Pick<KybDocument, 'kybDocumentId' | 'type' | 'status' | 'url'>
  >;
  capitalDeposit: Pick<
    CapitalDeposit,
    'status' | 'capitalDepositId' | 'partner'
  > & {
    paidUpCapitalPercentage: number;
  };
  legalBeneficiaries: Array<
    Pick<
      CompanyLegalBeneficiary,
      | 'companyLegalBeneficiaryId'
      | 'firstName'
      | 'lastName'
      | 'isCurrentUser'
      | 'numberOfShares'
    >
  >;
};

interface ApplicationFollowUpProps {
  applicationFollowUpData: ApplicationFollowUpData;
  isInitialSubmission: boolean;
}

const ApplicationFollowUp = asBentoPage<ApplicationFollowUpProps>(
  ({ applicationFollowUpData, isInitialSubmission, Layout }) => {
    /**
     * we want to always show 3 steps but change the registration
     * step to request signature if needed
     */
    const applicationRegistrationStep =
      applicationFollowUpData.creationSASU?.status ===
      CompanyCreationStatus.DossierSentToGuichetUnique
        ? applicationSentToStateStep
        : applicationSignatureStep;

    const applicationSteps = [
      applicationVerificationStep,
      applicationFundsStep,
      applicationRegistrationStep,
      applicationAccountOpeningStep,
    ];

    const activeStep = applicationSteps.findIndex((step) =>
      step.isActive(applicationFollowUpData),
    );

    return (
      <Layout
        nav={webView.config?.isEmbedInAppLayout ? null : <OBNavigation />}
      >
        <Header
          marginBottom="space-16"
          title={
            isInitialSubmission
              ? locales.initialSubmission.title
              : locales.title
          }
        />

        <Text marginBottom="space-48">
          {locales.formatString(locales.subtitle, {
            email: applicationFollowUpData.userProfile.email ?? '',
          })}
        </Text>

        {isInitialSubmission ? (
          <Text
            fontWeight="weight-500"
            marginBottom="space-24"
            size="lg"
            variant="primary"
          >
            {locales.initialSubmission.guideNextSteps}
          </Text>
        ) : null}

        <VStack marginBottom="space-48">
          {applicationSteps.map((step, index) => {
            const stepStatus = (() => {
              if (activeStep > index) {
                return 'done';
              }

              if (activeStep === index) {
                return 'active';
              }

              return 'todo';
            })();

            /**
             * If this screen is shown in the App home layout (in the mobile app),
             * we only want to show the active step.
             */
            if (webView.config?.isEmbedInAppLayout && stepStatus !== 'active') {
              return null;
            }

            return (
              <ApplicationStepCard
                activeBadgeLabel={step.activeBadgeLabel}
                key={step.title}
                status={stepStatus}
                stepNumber={index + 1}
                title={step.title}
              >
                <step.body
                  data={applicationFollowUpData}
                  stepStatus={stepStatus}
                />
              </ApplicationStepCard>
            );
          })}
        </VStack>

        {webView.config?.isEmbedInAppLayout ? null : (
          <Text>
            {`${locales.helpPrompt} `}
            <Link variant="inline" {...INTERCOM_LAUNCHER_PROPS}>
              {locales.helpLink}
            </Link>
          </Text>
        )}
      </Layout>
    );
  },
);

export default ApplicationFollowUp;
